import { useEffect, useRef, useState } from "react";
import { Music, Pause, Volume2, VolumeX } from "lucide-react";
import { useI18n } from "../i18n";

// Bellini — « Casta Diva » (Maria Callas). Fichier : /public/bellini.mp3
const SRC = "/bellini.mp3";
const FADE_MS = 3000;
const VOLUME_KEY = "prr-music-volume";
const DEFAULT_VOLUME = 0.5;

export const MusicPlayer = () => {
  const { t } = useI18n();
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [available, setAvailable] = useState(true);
  const [volume, setVolume] = useState(() => {
    const saved = Number(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(saved) && saved > 0 && saved <= 1 ? saved : DEFAULT_VOLUME;
  });

  useEffect(() => {
    // Hide the control if the audio file is missing.
    fetch(SRC, { method: "HEAD" })
      .then((r) => setAvailable(r.ok))
      .catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(VOLUME_KEY, String(volume));
  }, [volume]);

  useEffect(() => () => cancelFade(), []);

  const cancelFade = () => {
    if (fadeRef.current) {
      cancelAnimationFrame(fadeRef.current);
      fadeRef.current = null;
    }
  };

  const fadeIn = (audio, target) => {
    cancelFade();
    audio.volume = 0;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / FADE_MS, 1);
      audio.volume = target * p;
      if (p < 1) fadeRef.current = requestAnimationFrame(step);
      else fadeRef.current = null;
    };
    fadeRef.current = requestAnimationFrame(step);
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      cancelFade();
      a.pause();
      setPlaying(false);
    } else {
      a.play()
        .then(() => {
          setPlaying(true);
          fadeIn(a, volume > 0 ? volume : DEFAULT_VOLUME);
        })
        .catch(() => setPlaying(false));
    }
  };

  const onVolumeChange = (e) => {
    const v = Number(e.target.value) / 100;
    setVolume(v);
    cancelFade();
    const a = audioRef.current;
    if (a && playing) a.volume = v;
  };

  if (!available) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[60]">
      <audio ref={audioRef} src={SRC} loop preload="none" onEnded={() => setPlaying(false)} />
      <div className="flex items-center gap-1 bg-[#0B1121]/90 backdrop-blur text-white rounded-full shadow-2xl border border-white/10 pl-3 pr-4 py-2">
        <button
          data-testid="music-toggle"
          onClick={toggle}
          aria-label={t("music.play")}
          className="group flex items-center gap-2 hover:text-[#38BDF8] transition-colors"
        >
          <span className="relative flex items-center justify-center h-6 w-6">
            {playing ? <Pause size={16} /> : <Music size={16} />}
            {playing && <span className="absolute inset-0 rounded-full border border-[#38BDF8] animate-ping" />}
          </span>
          <span className="text-xs font-semibold whitespace-nowrap">
            {playing ? t("music.pause") : "Casta Diva"}
          </span>
        </button>
        <span className="mx-1 h-4 w-px bg-white/15" aria-hidden="true" />
        <label className="flex items-center gap-1.5" aria-label={t("music.volume")}>
          {volume === 0 ? <VolumeX size={14} className="text-white/60" /> : <Volume2 size={14} className="text-white/60" />}
          <input
            data-testid="music-volume"
            type="range"
            min="0"
            max="100"
            value={Math.round(volume * 100)}
            onChange={onVolumeChange}
            className="music-volume w-16 sm:w-20 cursor-pointer"
          />
        </label>
      </div>
    </div>
  );
};
